import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { Menu, X, Heart, Instagram, Facebook, Phone, Mail, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const mainLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Programs", path: "/programs" },
  { name: "Lift Her Up", path: "/lift-her-up" },
  { name: "Scholarship", path: "/scholarship" },
];

const moreLinks = [
  { name: "Workshops", path: "/workshops" },
  { name: "Calendar", path: "/calendar" },
  { name: "Partners", path: "/partners" },
  { name: "Referral Form", path: "/referral-form" },
  { name: "Contact", path: "/contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const onScroll = useCallback(() => setScrolled(window.scrollY > 30), []);
  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      className={`fixed top-4 left-4 right-4 z-50 rounded-full glass-nav transition-all duration-300 ${
        scrolled ? "shadow-lg" : ""
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0" data-testid="nav-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_soaryouth/artifacts/s2dzfns3_logo%20transparency%20file%20op%201.png"
            alt="SOAR Solutions"
            className="h-10 w-auto"
          />
        </Link>

        {/* Desktop Links */}
        <div className="hidden lg:flex items-center gap-1">
          {mainLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.name.toLowerCase().replace(/ /g, '-')}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                isActive(link.path)
                  ? "bg-soar-teal text-white"
                  : "text-soar-blue/70 hover:text-soar-blue hover:bg-soar-blue/5"
              }`}
            >
              {link.name}
            </Link>
          ))}

          {/* More Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-more-dropdown"
                className="px-4 py-2 rounded-full text-sm font-semibold text-soar-blue/70 hover:text-soar-blue hover:bg-soar-blue/5 flex items-center gap-1 transition-colors duration-200"
              >
                More <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-2xl bg-white border border-soar-blue/10 shadow-xl p-1 mt-2">
              {moreLinks.map((link) => (
                <DropdownMenuItem key={link.path} asChild>
                  <Link
                    to={link.path}
                    data-testid={`nav-${link.name.toLowerCase().replace(/ /g, '-')}`}
                    className="cursor-pointer rounded-xl px-4 py-2.5 text-sm font-medium text-soar-blue/70 hover:text-soar-blue hover:bg-soar-teal/5"
                  >
                    {link.name}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Donate CTA + Mobile Toggle */}
        <div className="flex items-center gap-3">
          <Link to="/donate" className="hidden sm:block" data-testid="nav-donate-btn">
            <Button className="rounded-full bg-soar-gold hover:bg-soar-gold/90 text-soar-blue font-bold px-6 py-2.5 text-sm hover:scale-105 transition-transform duration-200 shadow-md">
              <Heart className="w-4 h-4 mr-1.5" /> Donate
            </Button>
          </Link>
          <button
            className="lg:hidden p-2 rounded-xl text-soar-blue/70 hover:text-soar-blue hover:bg-soar-blue/5 transition-colors duration-200"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div className="lg:hidden mx-4 mb-4 mt-1 bg-white border border-soar-blue/10 rounded-2xl p-4 space-y-1 shadow-xl">
          {[...mainLinks, ...moreLinks].map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${link.name.toLowerCase().replace(/ /g, '-')}`}
              className={`block px-4 py-3 rounded-xl text-sm font-semibold transition-colors duration-200 ${
                isActive(link.path) ? "bg-soar-teal text-white" : "text-soar-blue/70 hover:text-soar-blue hover:bg-soar-teal/5"
              }`}
            >
              {link.name}
            </Link>
          ))}
          <Link to="/donate" onClick={() => setOpen(false)} data-testid="mobile-donate-btn" className="block mt-3">
            <Button className="w-full rounded-xl bg-soar-gold hover:bg-soar-gold/90 text-soar-blue font-bold py-3">
              <Heart className="w-4 h-4 mr-2" /> Donate
            </Button>
          </Link>
        </div>
      )}
    </nav>
  );
};

export const Footer = () => (
  <footer className="relative bg-soar-blue border-t border-soar-blue/10" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 py-20">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
        {/* Brand */}
        <div className="md:col-span-5 space-y-5">
          <img
            src="https://customer-assets.emergentagent.com/job_soaryouth/artifacts/s2dzfns3_logo%20transparency%20file%20op%201.png"
            alt="SOAR Solutions"
            className="h-14 w-auto"
          />
          <p className="text-white/60 text-sm leading-relaxed max-w-sm">
            Empowering youth ages 13-25 through mentorship, life skills, and community support.
          </p>
          <p className="font-accent text-2xl text-soar-gold">It&apos;s your life, grow it, shape it, own it.</p>
          <div className="flex gap-3 pt-2">
            <a href="https://instagram.com/soar_solutions" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white/60 hover:text-soar-gold hover:border-soar-gold/50 transition-colors duration-200" data-testid="social-instagram">
              <Instagram className="w-4 h-4" />
            </a>
            <a href="https://facebook.com/SoarSolutionsYouthCenter" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white/60 hover:text-soar-gold hover:border-soar-gold/50 transition-colors duration-200" data-testid="social-facebook">
              <Facebook className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Links */}
        <div className="md:col-span-3">
          <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40 mb-5">Navigate</h4>
          <ul className="space-y-3">
            {[...mainLinks, { name: "Donate", path: "/donate" }].map((l) => (
              <li key={l.path}><Link to={l.path} className="text-white/60 hover:text-soar-gold transition-colors duration-200 text-sm font-medium">{l.name}</Link></li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div className="md:col-span-4">
          <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40 mb-5">Get in Touch</h4>
          <div className="space-y-4">
            <a href="mailto:Info@soar-solutions.org" className="flex items-center gap-3 text-white/60 hover:text-soar-gold transition-colors duration-200 text-sm font-medium">
              <Mail className="w-4 h-4 text-soar-teal" /> Info@soar-solutions.org
            </a>
            <a href="tel:661-401-5031" className="flex items-center gap-3 text-white/60 hover:text-soar-gold transition-colors duration-200 text-sm font-medium">
              <Phone className="w-4 h-4 text-soar-teal" /> 661-401-5031
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-white/40 text-xs">&copy; {new Date().getFullYear()} Soar Solutions Youth Center. All rights reserved.</p>
        <p className="text-white/40 text-xs">501(c)(3) Nonprofit Organization</p>
      </div>
    </div>
  </footer>
);

const Layout = () => (
  <div className="min-h-screen flex flex-col bg-white">
    <Navbar />
    <main className="flex-1 pt-24">
      <Outlet />
    </main>
    <Footer />
  </div>
);

export default Layout;
